













































































import { defineComponent, PropType, ref } from '@vue/composition-api';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import { useVModel } from '@vueuse/core';
import { debounce } from 'lodash';

import {
  IWorkflowTransition,
  IWorkflowTransitionData,
  WorkflowTransition
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { PromiseQueue } from '@/models/promise-queue/promise-queue';

import { IRootState } from '@/store';
import {
  IWorkflowStoreState,
  IWorkflowGetters,
  IWorkflowActions,
  IWorkflowMutations
} from '@/store/workflow';

import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import { SystemError } from '@/models/error/error';
import { ALForm } from '@/common/component.types';

import { VForm } from 'vuetify/lib/components';

export default defineComponent({
  name: 'WorkflowTransitionForm',

  props: {
    workflowId: { type: Number, required: true },
    value: {
      type: Object as PropType<IWorkflowTransitionData>,
      required: true
    },
    disabled: {
      type: Boolean
    }
  },

  setup(props, { emit }) {
    const workflowTransitionData = useVModel(props, 'value');

    const promiseQueue = new PromiseQueue();

    const store = useStore<IRootState>();

    const { useActions, useGetters } = createNamespacedHelpers<
      IWorkflowStoreState,
      IWorkflowGetters,
      IWorkflowActions,
      IWorkflowMutations
    >(store, 'workflow');

    const { getWorkflowTransitionById } = useGetters([
      'getWorkflowTransitionById'
    ]);

    const { updateWorkflowTransition } = useActions([
      'updateWorkflowTransition'
    ]);

    const isFormValid = ref(false);
    const formRef = ref<InstanceType<typeof VForm> & ALForm>();

    const error = ref<ISystemError | null>(null);

    function clearError() {
      error.value = null;
    }

    const isLoading = ref(false);

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function saveWorkflowTransition() {
      clearError();

      const valid = validateForm();

      if (valid) {
        isLoading.value = true;
        let workflowTransitionInstance: IWorkflowTransition | null = null;

        try {
          workflowTransitionInstance = new WorkflowTransition(
            workflowTransitionData.value
          );
        } catch (err) {
          if (SystemError.isSystemError(err)) {
            error.value = err;
          }
        }
        if (workflowTransitionInstance != null) {
          const payloadDelta = diffDeep(
            workflowTransitionInstance,
            getWorkflowTransitionById.value(workflowTransitionInstance.id!),
            true
          );

          updateWorkflowTransition({
            workflowId: props.workflowId!,
            transition: payloadDelta
          })
            .then(() => {
              emit(
                'update-workflow-transition-data',
                payloadDelta.id!.toString(),
                getWorkflowTransitionById.value(workflowTransitionInstance!.id!)
              );
            })
            .catch(err => {
              error.value = setErrorFromAxiosError(err);
            })
            .finally(() => {
              isLoading.value = false;
            });
        }
      }
    }
    const debounceSaveWorkflowTransition = debounce(() => {
      promiseQueue.clearQueue();

      promiseQueue.enqueue(() => saveWorkflowTransition());
    }, 1000);

    return {
      formRef,

      workflowTransitionData,
      isFormValid,
      isLoading,
      requiredRule: [(v: string) => !!v || 'Field is required'],
      error,
      clearError,

      debounceSaveWorkflowTransition
    };
  }
});
